<div class="wrapper-express-report">
  <!-- Page Header -->
  <div class="section-title">
    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
      <div class="d-flex align-items-center">
        <div class="title">{{ "Express Checkout Report" | translate }}</div>
      </div>

      <!-- Quick Action Navigation in Header -->
      <div class="header-actions topbar-actions d-flex align-items-center flex-wrap gap-2">
        <button
          type="button"
          class="btn btn-action-ai ui-classic-only"
          [disabled]="isGeneratingAiSummary"
          (click)="generateAiSummary($event)"
          data-ai-purpose="generate summary"
          [title]="'Summarize Express Checkout Report with AI Assistant' | translate"
        >
          <i class="fi fi-rr-sparkles mr-1" [class.fa-spin]="isGeneratingAiSummary"></i>
          <span>{{ (isGeneratingAiSummary ? "Generating Summary..." : "AI Summary") | translate }}</span>
          <span class="ai-coin-token" *ngIf="!isGeneratingAiSummary && aiTokenCost" [attr.title]="aiTokenCost + ' tokens'">
            {{ aiTokenCost }}
          </span>
        </button>
        <button
          type="button"
          class="btn btn-action-excel ui-classic-only"
          (click)="download('excel')"
          [disabled]="isExporting"
          title="{{ 'Export to Excel' | translate }}"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-1.5 flex-shrink-0">
            <path fill="#107C41" d="M14 2H6C4.89 2 4 2.89 4 4V20C4 21.11 4.89 22 6 22H18C19.11 22 20 21.11 20 20V8L14 2Z"/>
            <path fill="#FFFFFF" opacity="0.6" d="M14 2L20 8H14V2Z"/>
            <path fill="#FFFFFF" d="M9.6 16.5L11.5 13.6L13.4 16.5H15.5L12.6 12.3L15.3 8.5H13.2L11.5 11L9.8 8.5H7.7L10.4 12.3L7.5 16.5H9.6Z"/>
          </svg>
          <span *ngIf="!isExporting">{{ "Export Excel" | translate }}</span>
          <span *ngIf="isExporting">{{ "Retrieving...please wait" | translate }}</span>
        </button>
        <a
          routerLink="/dashboard/express-checkout"
          class="btn btn-action-secondary ui-classic-only"
        >
          <i class="fi fi-rr-list mr-1"></i>
          {{ "Manage Links" | translate }}
        </a>
        <a
          routerLink="/dashboard/express-checkout-create"
          class="btn btn-action-primary"
        >
          <i class="fi fi-rr-plus mr-1"></i>
          {{ "Create Checkout Link" | translate }}
        </a>
      </div>
    </div>
  </div>

  <div class="section-main mt-4">
    <!-- Report Guidance & Overview Banner -->
    <div class="report-guide-card mb-4">
      <div class="guide-header d-flex align-items-center mb-2">
        <div class="guide-icon mr-2">
          <i class="fi fi-rr-chart-line-up"></i>
        </div>
        <div>
          <h2 class="guide-heading font-weight-bold mb-0">
            {{ "Report Purpose & Insights" | translate }}
          </h2>
        </div>
        <!-- Glass theme: the report actions sit in this card (the page title is in the top bar) -->
        <div class="guide-actions ui-glass-only d-flex align-items-center flex-wrap gap-2 ml-auto">
          <button
            type="button"
            class="btn btn-action-ai"
            [disabled]="isGeneratingAiSummary"
            (click)="generateAiSummary($event)"
            data-ai-purpose="generate summary"
            [title]="'Summarize Express Checkout Report with AI Assistant' | translate"
          >
            <i class="fi fi-rr-sparkles mr-1" [class.fa-spin]="isGeneratingAiSummary"></i>
            <span>{{ (isGeneratingAiSummary ? "Generating Summary..." : "AI Summary") | translate }}</span>
            <span class="ai-coin-token" *ngIf="!isGeneratingAiSummary && aiTokenCost" [attr.title]="aiTokenCost + ' tokens'">
            {{ aiTokenCost }}
            </span>
          </button>
          <button
            type="button"
            class="btn btn-action-excel"
            (click)="download('excel')"
            [disabled]="isExporting"
            title="{{ 'Export to Excel' | translate }}"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-1.5 flex-shrink-0">
            <path fill="#107C41" d="M14 2H6C4.89 2 4 2.89 4 4V20C4 21.11 4.89 22 6 22H18C19.11 22 20 21.11 20 20V8L14 2Z"/>
            <path fill="#FFFFFF" opacity="0.6" d="M14 2L20 8H14V2Z"/>
            <path fill="#FFFFFF" d="M9.6 16.5L11.5 13.6L13.4 16.5H15.5L12.6 12.3L15.3 8.5H13.2L11.5 11L9.8 8.5H7.7L10.4 12.3L7.5 16.5H9.6Z"/>
            </svg>
            <span *ngIf="!isExporting">{{ "Export Excel" | translate }}</span>
            <span *ngIf="isExporting">{{ "Retrieving...please wait" | translate }}</span>
          </button>
          <a
            routerLink="/dashboard/express-checkout"
            class="btn btn-action-secondary"
          >
            <i class="fi fi-rr-list mr-1"></i>
            {{ "Manage Links" | translate }}
          </a>
        </div>
      </div>
      <p class="intro-text mb-3">
        {{ "Track and analyze the performance of your Express Checkout direct links: monitor conversion rates, identify abandoned checkouts, analyze link creation trends, and discover top-performing products and link campaigns." | translate }}
      </p>
      <div class="guide-pills d-flex flex-wrap gap-2">
        <div class="guide-pill">
          <div class="pill-badge pill-badge-primary mr-2">
            <i class="fi fi-rr-bullseye"></i>
          </div>
          <div class="pill-text">
            <span class="guide-pill-title">{{ "Purpose:" | translate }}</span>
            <span class="guide-pill-desc ml-1">{{ "Measure direct checkout volume, link efficiency, and order completions without full store navigation." | translate }}</span>
          </div>
        </div>
        <div class="guide-pill">
          <div class="pill-badge pill-badge-success mr-2">
            <i class="fi fi-rr-bulb"></i>
          </div>
          <div class="pill-text">
            <span class="guide-pill-title">{{ "When to Use:" | translate }}</span>
            <span class="guide-pill-desc ml-1">{{ "Use when running direct ads, influencer promotions, or social campaigns to evaluate link ROI, diagnose drop-offs, and scale top-selling products." | translate }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Performance Summary & Insights Card -->
    <div
      class="ai-summary-card mb-4"
      *ngIf="showAiSummaryCard"
    >
      <div class="ai-summary-header d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div class="d-flex align-items-center">
          <div class="ai-icon-badge mr-2">
            <i class="fi fi-rr-sparkles"></i>
          </div>
          <div>
            <div class="ai-title font-weight-bold">
              {{ "AI Performance Summary & Insights" | translate }}
            </div>
            <div class="ai-subtitle text-muted">
              {{ "Automated AI analysis based on current express checkout conversions and campaign metrics" | translate }}
            </div>
          </div>
        </div>

        <div class="ai-actions d-flex align-items-center gap-2">
          <button
            *ngIf="!isGeneratingAiSummary && aiSummaryContent && !aiSummaryError"
            type="button"
            class="btn btn-sm btn-ai-action"
            (click)="copyAiSummary()"
            [title]="'Copy to Clipboard' | translate"
          >
            <i class="fi" [ngClass]="isCopied ? 'fi-rr-check' : 'fi-rr-copy'"></i>
            <span class="ml-1">{{ (isCopied ? "Copied!" : "Copy") | translate }}</span>
          </button>
          <button
            *ngIf="!isGeneratingAiSummary"
            type="button"
            class="btn btn-sm btn-ai-action"
            (click)="generateAiSummary($event)"
            data-ai-purpose="generate summary"
            [title]="'Regenerate Summary' | translate"
          >
            <i class="fi fi-rr-refresh"></i>
            <span class="ml-1">{{ "Regenerate" | translate }}</span>
            <span class="ai-coin-token ai-coin-sm" *ngIf="aiTokenCost" [attr.title]="aiTokenCost + ' tokens'">
              {{ aiTokenCost }}
            </span>
          </button>
          <button
            type="button"
            class="btn btn-sm btn-ai-close"
            (click)="dismissAiSummary()"
            [attr.aria-label]="'Close' | translate"
          >
            <i class="fi fi-rr-cross"></i>
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div *ngIf="isGeneratingAiSummary" class="ai-loading-container py-4 text-center">
        <div class="ai-spinner mb-3">
          <i class="fi fi-rr-sparkles fa-spin"></i>
        </div>
        <div class="font-weight-bold text-primary mb-1">
          {{ "Analyzing express checkout metrics & generating insights..." | translate }}
        </div>
        <div class="text-muted small">
          {{ "Evaluating link conversions, cart abandonments, and top-selling campaigns" | translate }}
        </div>
        <div class="ai-skeleton-box mt-3">
          <div class="skeleton-line full"></div>
          <div class="skeleton-line three-quarters"></div>
          <div class="skeleton-line half"></div>
        </div>
      </div>

      <!-- Error State -->
      <div *ngIf="!isGeneratingAiSummary && aiSummaryError" class="ai-error-container p-3 my-2">
        <div class="d-flex align-items-center text-danger mb-2">
          <i class="fi fi-rr-exclamation mr-2"></i>
          <span class="font-weight-bold">{{ aiSummaryError }}</span>
        </div>
        <button type="button" class="btn btn-sm btn-primary mt-1" (click)="generateAiSummary($event)" data-ai-purpose="generate summary">
          {{ "Try Again" | translate }}
        </button>
      </div>

      <!-- AI Content Display -->
      <div
        *ngIf="!isGeneratingAiSummary && !aiSummaryError && aiSummaryContent"
        class="ai-summary-body mt-3"
      >
        <!-- Structured Insights Cards Grid -->
        <div class="ai-insights-grid" *ngIf="parsedSections && parsedSections.length > 0">
          <div
            *ngFor="let sec of parsedSections"
            class="ai-insight-card"
            [ngClass]="'theme-' + sec.theme"
          >
            <div class="ai-card-header">
              <span class="ai-card-badge">
                <i class="fi" [ngClass]="sec.icon"></i>
              </span>
              <h4 class="ai-card-title">{{ sec.title | translate }}</h4>
            </div>
            <div class="ai-card-body">
              <div *ngFor="let row of sec.rows" class="ai-row-item">
                <span
                  *ngIf="sec.theme === 'recommendations' && row.index"
                  class="item-num-badge"
                >{{ row.index }}</span>
                <span
                  *ngIf="sec.theme !== 'recommendations' || !row.index"
                  class="item-bullet"
                ></span>
                <div class="item-content">
                  <span *ngIf="row.label" class="ai-label">{{ row.label }}:</span>
                  <span *ngIf="row.value" class="ai-val-highlight ml-1">{{ row.value }}</span>
                  <span *ngIf="row.note" class="ai-note ml-1"><ng-container *ngIf="row.value">– </ng-container>{{ row.note }}</span>
                  <span *ngIf="row.text && !row.label" class="ai-text">{{ row.text }}</span>
                  <a
                    *ngIf="row.url"
                    [href]="row.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="ai-url-pill ml-1"
                    [attr.title]="row.url"
                  >
                    <i class="fi fi-rr-arrow-square-right mr-1"></i>
                    <span>{{ displayUrl(row.url) }}</span>
                  </a>
                  <span *ngIf="row.url && row.text && row.label" class="ai-url-extra ml-1 text-muted">
                    {{ row.text }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Raw Fallback if no sections were parsed -->
        <div *ngIf="!parsedSections || parsedSections.length === 0" class="ai-fallback-text p-3">
          <div class="small text-muted" style="white-space: pre-wrap;">{{ aiSummaryContent }}</div>
        </div>
      </div>
    </div>

    <!-- Summary KPI Stat Cards -->
    <div class="row summary-row mb-4">
      <!-- Conversion Rate -->
      <div class="col-12 col-md-4 mb-3 mb-md-0">
        <div class="stat-card stat-card-rate h-100">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <div class="stat-label">{{ "Link Conversion Rate" | translate }}</div>
              <div class="stat-value mt-2">
                {{ coversionRate !== undefined && coversionRate !== null ? coversionRate : 0 }}%
              </div>
            </div>
            <div class="stat-icon-wrapper icon-rate">
              <i class="fi fi-rr-chart-pie-alt"></i>
            </div>
          </div>
          <div class="stat-footer mt-3">
            <span class="stat-pill stat-pill-success mr-2">
              <i class="fi fi-rr-check"></i>
            </span>
            <span class="stat-subtext">{{ "Checkout completion rate" | translate }}</span>
          </div>
        </div>
      </div>

      <!-- Abandoned Checkouts -->
      <div class="col-12 col-md-4 mb-3 mb-md-0">
        <div class="stat-card stat-card-abandoned h-100">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <div class="stat-label">{{ "Abandoned Checkouts" | translate }}</div>
              <div class="stat-value mt-2">
                {{ abandonedOrders !== undefined && abandonedOrders !== null ? abandonedOrders : 0 }}
              </div>
            </div>
            <div class="stat-icon-wrapper icon-abandoned">
              <i class="fi fi-rr-shopping-cart-check"></i>
            </div>
          </div>
          <div class="stat-footer mt-3">
            <span class="stat-pill stat-pill-warning mr-2">
              <i class="fi fi-rr-time-forward"></i>
            </span>
            <span class="stat-subtext">{{ "Orders pending completion" | translate }}</span>
          </div>
        </div>
      </div>

      <!-- Number of Links Created -->
      <div class="col-12 col-md-4">
        <div class="stat-card stat-card-links h-100">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <div class="stat-label">{{ "Number of Link Created" | translate }}</div>
              <div class="stat-value mt-2">
                {{ totalCreatedLink !== undefined && totalCreatedLink !== null ? totalCreatedLink : 0 }}
              </div>
            </div>
            <div class="stat-icon-wrapper icon-links">
              <i class="fi fi-rr-link-alt"></i>
            </div>
          </div>
          <div class="stat-footer mt-3">
            <span class="stat-pill stat-pill-info mr-2">
              <i class="fi fi-rr-apps"></i>
            </span>
            <span class="stat-subtext">{{ "Total active checkout links" | translate }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Time-Based Analysis Chart Card -->
    <div class="card report-chart-card mb-4">
      <div class="card-header-report d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div class="chart-header-group">
          <div class="d-flex align-items-center">
            <div class="chart-header-icon mr-2">
              <i class="fi fi-rr-stats"></i>
            </div>
            <h2 class="card-report-title mb-0">{{ "Time-Based Analysis" | translate }}</h2>
          </div>
          <p class="card-report-subtitle mb-0 mt-1 text-muted">
            {{ "Monitor checkout link generation and activity volume over time" | translate }}
          </p>
        </div>
        <div class="chart-filter-nav d-flex flex-wrap">
          <button
            type="button"
            class="nav-chart-pill"
            [ngClass]="{ active: currentMonthActive === 'nav-active' }"
            (click)="toggleNav('CurrentMonth')"
          >
            {{ "Current Month" | translate }}
          </button>
          <button
            type="button"
            class="nav-chart-pill"
            [ngClass]="{ active: todayActive === 'nav-active' }"
            (click)="toggleNav('Today')"
          >
            {{ "Today" | translate }}
          </button>
          <button
            type="button"
            class="nav-chart-pill"
            [ngClass]="{ active: last14DaysActive === 'nav-active' }"
            (click)="toggleNav('Last14Days')"
          >
            {{ "Last 14 Days" | translate }}
          </button>
          <button
            type="button"
            class="nav-chart-pill"
            [ngClass]="{ active: last30DaysActive === 'nav-active' }"
            (click)="toggleNav('Last30Days')"
          >
            {{ "Last 30 Days" | translate }}
          </button>
        </div>
      </div>

      <div class="card-body-chart pt-3">
        <div class="chart-container">
          <apx-chart
            [chart]="apxchart.chart"
            [dataLabels]="apxchart.dataLabels"
            [stroke]="apxchart.stroke"
            [fill]="apxchart.fill"
            [series]="apxchart.series"
            [title]="apxchart.title"
            [colors]="apxchart.colors"
            [labels]="apxchart.labels"
            [xaxis]="apxchart.xaxis"
            [yaxis]="apxchart.yaxis"
            [legend]="apxchart.legend"
            [tooltip]="apxchart.tooltip"
          >
          </apx-chart>
        </div>
      </div>
    </div>

    <!-- Insights Grid: Popular Products & Popular Links -->
    <div class="row mb-4">
      <!-- Popular Products -->
      <div class="col-12 col-lg-6 mb-4 mb-lg-0">
        <div class="card insight-card h-100">
          <div class="insight-header d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <div class="insight-icon-box bg-soft-primary text-primary mr-2">
                <i class="fi fi-rr-box-alt"></i>
              </div>
              <div>
                <h2 class="card-report-title mb-0">{{ "Popular Products" | translate }}</h2>
                <span class="card-report-caption text-muted">{{ "Highest ordering products via direct links" | translate }}</span>
              </div>
            </div>
            <button
              type="button"
              class="btn-see-more"
              (click)="seeMoreClick()"
            >
              {{ "See More" | translate }}
              <i class="fi fi-rr-arrow-small-right ml-1"></i>
            </button>
          </div>

          <!-- Country Filter Dropdown -->
          <div class="country-filter-wrap mt-3">
            <div ngbDropdown class="d-block w-100">
              <button
                class="btn country-select-btn d-flex align-items-center justify-content-between w-100"
                id="dropdownProductCountry"
                ngbDropdownToggle
              >
                <div class="d-flex align-items-center overflow-hidden">
                  <div class="country-flag-wrap mr-2.5" *ngIf="countryFlag">
                    <img
                      class="country-flag"
                      [src]="countryFlag"
                      [alt]="countryName"
                    />
                  </div>
                  <span class="country-name-text text-truncate">{{ countryName | translate }}</span>
                </div>
                <i class="fi fi-rr-angle-small-down ml-2"></i>
              </button>
              <div
                ngbDropdownMenu
                aria-labelledby="dropdownProductCountry"
                class="custom-dropdown shadow-sm w-100 country-menu-scroll"
              >
                <button
                  ngbDropdownItem
                  *ngFor="let country of filteredProductCountry"
                  (click)="changeCountry(country.country_code)"
                  class="d-flex align-items-center country-dropdown-item"
                >
                  <div class="country-flag-wrap mr-2.5" *ngIf="country.country_flag">
                    <img
                      class="country-flag"
                      [src]="country.country_flag"
                      [alt]="country.country_name"
                    />
                  </div>
                  <span class="country-dropdown-text">{{ country.country_name | translate }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Product List -->
          <div class="insight-list-wrap mt-3">
            <div *ngIf="productFilterCountryList?.length">
              <div
                class="insight-item-row d-flex align-items-center"
                *ngFor="let product of productFilterCountryList; let i = index"
              >
                <div class="rank-badge mr-2" [ngClass]="'rank-' + (i + 1)">#{{ i + 1 }}</div>
                <div class="product-thumb-wrap mr-3">
                  <img
                    [src]="product.image"
                    [alt]="product.product_name"
                    class="product-thumb"
                  />
                </div>
                <div class="item-title flex-grow-1 text-truncate mr-2" [title]="product.product_name">
                  {{ product.product_name }}
                </div>
                <div class="order-badge">
                  <i class="fi fi-rr-shopping-bag mr-1"></i>
                  {{ product.quantity }} {{ "Orders" | translate }}
                </div>
              </div>
            </div>

            <div *ngIf="!productFilterCountryList?.length" class="empty-state py-4 text-center">
              <i class="fi fi-rr-box empty-icon"></i>
              <p class="mb-0 mt-2 text-muted">{{ "No record found." | translate }}</p>
            </div>
          </div>

          <!-- Explanatory note -->
          <div class="insight-note-box mt-3">
            <i class="fi fi-rr-info mr-2 note-icon"></i>
            <span>{{ "This display features globally popular products, curated based on their widespread demand and appeal. Explore these top-rated items to stay in the know about what's trending worldwide" | translate }}</span>
          </div>
        </div>
      </div>

      <!-- Popular Links (Top 5) -->
      <div class="col-12 col-lg-6">
        <div class="card insight-card h-100">
          <div class="insight-header d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <div class="insight-icon-box bg-soft-info text-info mr-2">
                <i class="fi fi-rr-link-alt"></i>
              </div>
              <div>
                <h2 class="card-report-title mb-0">{{ "Popular Link (Top 5)" | translate }}</h2>
                <span class="card-report-caption text-muted">{{ "Top converted campaign & product links" | translate }}</span>
              </div>
            </div>
            <button
              type="button"
              class="btn-see-more"
              (click)="seeMoreClick()"
            >
              {{ "See More" | translate }}
              <i class="fi fi-rr-arrow-small-right ml-1"></i>
            </button>
          </div>

          <!-- Links List -->
          <div class="insight-list-wrap mt-3">
            <div *ngIf="popularLink?.length">
              <div
                class="insight-item-row d-flex align-items-center"
                *ngFor="let link of popularLink; let i = index"
              >
                <div class="rank-badge mr-2" [ngClass]="'rank-' + (i + 1)">#{{ i + 1 }}</div>
                <div class="link-flag-wrap mr-3">
                  <img
                    class="link-flag"
                    [src]="getCountryFlag(link.country_code)"
                    [alt]="link.country_code"
                  />
                </div>
                <div class="item-title flex-grow-1 text-truncate mr-2" [title]="link.name">
                  {{ link.name }}
                </div>
                <div class="order-badge order-badge-success">
                  <i class="fi fi-rr-check mr-1"></i>
                  {{ link.quantity }} {{ "Orders" | translate }}
                </div>
              </div>
            </div>

            <div *ngIf="!getFilteredLinks()?.length" class="empty-state py-4 text-center">
              <i class="fi fi-rr-link-slash empty-icon"></i>
              <p class="mb-0 mt-2 text-muted">{{ "No record found." | translate }}</p>
            </div>
          </div>

          <!-- Explanatory note -->
          <div class="insight-note-box mt-3">
            <i class="fi fi-rr-shield-check mr-2 note-icon"></i>
            <span>{{ "These popular Express Checkout links have a track record of successful conversions into sales. Shop with confidence!" | translate }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Export & Actions Footer Section (Moved to Bottom as Requested) -->
    <div class="card export-footer-card mb-4">
      <div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
        <div class="export-footer-info d-flex align-items-center">
          <div class="export-icon-box mr-3">
            <i class="fi fi-rr-download"></i>
          </div>
          <div>
            <h3 class="export-footer-title mb-1">{{ "Export Report Data" | translate }}</h3>
            <p class="export-footer-desc text-muted mb-0">
              {{ "Download complete performance analytics, link conversions, and order metrics for offline reporting." | translate }}
            </p>
          </div>
        </div>
        <div class="export-footer-actions d-flex align-items-center flex-wrap gap-2 mt-2 mt-md-0">
          <button
            type="button"
            class="btn btn-action-excel"
            (click)="download('excel')"
            [disabled]="isExporting"
            title="{{ 'Export to Excel' | translate }}"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-1.5 flex-shrink-0">
              <path fill="#107C41" d="M14 2H6C4.89 2 4 2.89 4 4V20C4 21.11 4.89 22 6 22H18C19.11 22 20 21.11 20 20V8L14 2Z"/>
              <path fill="#FFFFFF" opacity="0.6" d="M14 2L20 8H14V2Z"/>
              <path fill="#FFFFFF" d="M9.6 16.5L11.5 13.6L13.4 16.5H15.5L12.6 12.3L15.3 8.5H13.2L11.5 11L9.8 8.5H7.7L10.4 12.3L7.5 16.5H9.6Z"/>
            </svg>
            <span *ngIf="!isExporting">{{ "Export Excel" | translate }}</span>
            <span *ngIf="isExporting">{{ "Retrieving...please wait" | translate }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</div>

